import React, { useEffect, useState } from 'react';
import { BASS_STYLES, DRUM_VOICES, PRESETS, bassSoundName, bassStyleName, drumSoundName, presetName, voiceName } from './patterns';
import { BASS_SOUNDS } from './sampler';
import { CHORD_BARS } from './use-backing-track';
import { MicLevel, Segmented, TempoControl } from '../practice-dock/controls';
import { KeyHint, spaceLabel } from '../practice-dock/shortcuts';

export { default as useBackingTrack } from './use-backing-track';

const chordBarsName = bars => ({
    0.5: gettext('Half a bar'),
    1: gettext('1 bar'),
    2: gettext('2 bars'),
}[bars]);

// The chords of a looped part, without repeats: "Am C G F".
function sectionChords(track) {
    const { section, chordNames } = track;
    return chordNames.slice(section.start, section.end + 1)
        .filter((name, i, names) => i === 0 || names[i - 1] !== name)
        .join(' ');
}

const SPEED_STEPS = [1, 2, 3, 5];

// The backing track's panel in the practice dock. The track itself (see
// useBackingTrack) belongs to the dock, so closing this panel doesn't stop it.
const BackingTrack = ({ track, transport }) => {
    const [editing, setEditing] = useState(false);
    const { settings, live, beat, preset, grid, bassStyle, bassSound, playing, loading, section } = track;
    const { trainer } = transport.metronome;
    const speedingUp = transport.trainerOn && trainer.every === 'loop';

    // Fetch the recorded sounds once, when the panel first opens.
    useEffect(() => {
        track.preload();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const counting = playing && beat && beat.bar < 0;
    const micMessage = {
        requesting: gettext('Allow the microphone so the band can hear which chord you play.'),
        denied: gettext('The microphone is blocked. Allow it from the address bar, or switch to tapping the chords.'),
        unsupported: gettext("This browser can't listen. Tap the chords instead."),
        error: gettext("Couldn't open the microphone."),
    }[live.micState];

    let hint = '';
    if (settings.source === 'tap') {
        hint = gettext('Arrow keys or a page-turner pedal step through the song.');
    } else if (settings.source === 'auto') {
        hint = gettext('The band plays through the chords in order. Play along!');
    } else if (micMessage) {
        hint = micMessage;
    } else if (playing && live.micState === 'on' && !live.chord) {
        hint = gettext('Start playing. The bass joins in when it hears a chord.');
    } else if (!playing) {
        hint = settings.headphones ? gettext('Strum along and the bass follows your chords.') : gettext('Works best with headphones.');
    }

    return (
        <div className="backing-track practice-tool">
            <div className="backing-track-transport">
                <button
                    type="button"
                    className={`practice-play ${playing ? 'playing' : ''} ${loading ? 'loading' : ''}`}
                    onClick={playing ? track.stop : track.start}
                    aria-label={playing ? gettext('Stop') : gettext('Play')}
                    aria-busy={loading}
                >
                    {playing ? <span className="icon-stop" /> : <span className="icon-play" />}
                    <KeyHint k={spaceLabel()} />
                </button>
                <TempoControl transport={transport} />
                <div className="backing-track-now" aria-live="polite">
                    {loading && !counting ?
                        <span className="loading">{gettext('Loading sounds…')}</span>
                        : counting ?
                            <span className="count">{beat.beat + 1}</span>
                            :
                            <span className="chord">{playing && live.chord ? live.chord : '–'}</span>
                    }
                    <span className="beats">
                        {Array.from({ length: preset.beats }, (_, i) => (
                            <span key={i} className={`dot ${playing && beat && beat.beat === i ? 'on' : ''}`} />
                        ))}
                    </span>
                </div>
            </div>

            {!track.isSubscriber &&
                <p className="practice-hint practice-subscribe">{gettext('The backing track is for subscribers.')}</p>
            }

            <div className="practice-selects">
                <label>
                    <span>{gettext('Groove')}</span>
                    <select className="practice-select" value={preset.id} onChange={e => track.update({ presetId: e.target.value })}>
                        {PRESETS.map(p => <option key={p.id} value={p.id}>{presetName(p.id)}</option>)}
                    </select>
                </label>
                <label>
                    <span>{gettext('Bass')}</span>
                    <select className="practice-select" value={bassStyle} onChange={e => track.updateGroove({ bass: e.target.value })}>
                        {BASS_STYLES.map(id => <option key={id} value={id}>{bassStyleName(id)}</option>)}
                    </select>
                </label>
            </div>

            <div className="practice-row">
                <Segmented
                    label={gettext('Chords from')}
                    value={settings.source}
                    onChange={track.setSource}
                    options={[
                        { value: 'listen', label: gettext('Listen to me') },
                        { value: 'tap', label: gettext('Tap chords') },
                        { value: 'auto', label: gettext('Play them') },
                    ]}
                />
                {settings.source === 'listen' &&
                    <label className="practice-check">
                        <input type="checkbox" checked={settings.headphones} onChange={e => track.setHeadphones(e.target.checked)} />
                        {gettext("I'm using headphones")}
                    </label>
                }
                {settings.source === 'listen' && live.micState === 'on' && <MicLevel level={live.level} />}
                {settings.source === 'auto' &&
                    <label className="practice-inline">
                        {gettext('Each chord lasts')}
                        <select className="practice-select" value={settings.chordBars} onChange={e => track.update({ chordBars: Number(e.target.value) })}>
                            {CHORD_BARS.map(bars => <option key={bars} value={bars}>{chordBarsName(bars)}</option>)}
                        </select>
                    </label>
                }
            </div>

            {settings.source === 'tap' &&
                <div className="backing-track-chords">
                    {track.songChords.map((name, i) => (
                        <button type="button" key={name} className={`practice-pill ${playing && live.chord === name ? 'active' : ''}`} onClick={() => track.selectChord(name)} title={i < 9 ? `${i + 1}` : undefined}>{name}{i < 9 && <KeyHint k={String(i + 1)} />}</button>
                    ))}
                </div>
            }

            {hint && <p className="practice-hint">{hint}</p>}

            <div className="practice-loop">
                {section ?
                    <>
                        <div className="practice-loop-now">
                            <span className="practice-loop-icon" aria-hidden="true">⟲</span>
                            <span>{gettext('Looping')} <strong>{sectionChords(track)}</strong></span>
                            {playing && track.passes > 0 && <span className="practice-loop-passes">{interpolate(gettext('Time round: %s'), [track.passes + 1])}</span>}
                            <button type="button" className="practice-pill" onClick={track.clearLoop}>{gettext('Whole song')}</button>
                        </div>
                        <label className="practice-check">
                            <input
                                type="checkbox"
                                checked={speedingUp}
                                onChange={e => {
                                    // Speeding up is the metronome's speed trainer: for subscribers.
                                    if (e.target.checked && !track.isSubscriber) {
                                        track.requireSubscription();
                                        return;
                                    }
                                    transport.setMetronome({ trainer: e.target.checked ? { on: true, every: 'loop' } : { on: false } });
                                }}
                            />
                            {gettext('Speed up each time round')}
                        </label>
                        {speedingUp &&
                            <div className="practice-inline practice-indent">
                                <select className="practice-select" aria-label={gettext('BPM faster each time')} value={trainer.step} onChange={e => transport.setMetronome({ trainer: { step: Number(e.target.value) } })}>
                                    {SPEED_STEPS.map(step => <option key={step} value={step}>+{step} BPM</option>)}
                                </select>
                                {gettext('up to')}
                                <input
                                    type="number"
                                    className="practice-number"
                                    min="40"
                                    max="240"
                                    value={trainer.to}
                                    aria-label={gettext('Fastest tempo')}
                                    onChange={e => transport.setMetronome({ trainer: { to: Number(e.target.value) || trainer.to } })}
                                />
                                BPM
                            </div>
                        }
                    </>
                    :
                    <>
                        <button type="button" className="practice-pill" disabled={!track.selection} onClick={track.loopSelection}>
                            ⟲ {gettext('Loop the selected lines')}
                        </button>
                        {!track.selection && <p className="practice-hint">{gettext('Select some lines in the song to practise just that part over and over.')}</p>}
                    </>
                }
            </div>

            <button type="button" className="practice-link" aria-expanded={editing} onClick={() => setEditing(!editing)}>
                {gettext('Adjust')} {editing ? '▴' : '▾'}
            </button>

            {editing &&
                <div className="backing-track-editor">
                    <div className="practice-selects">
                        <label>
                            <span>{gettext('Drum sound')}</span>
                            <select className="practice-select" value={settings.drumSound} onChange={e => track.update({ drumSound: e.target.value })}>
                                {['acoustic', 'synth'].map(id => <option key={id} value={id}>{drumSoundName(id)}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>{gettext('Bass sound')}</span>
                            <select className="practice-select" value={bassSound} onChange={e => track.updateGroove({ bassSound: e.target.value })}>
                                {BASS_SOUNDS.map(id => <option key={id} value={id}>{bassSoundName(id)}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="backing-track-mix">
                        <label>{gettext('Drums')}<input type="range" min="0" max="1" step="0.05" value={settings.drums} onChange={e => track.update({ drums: Number(e.target.value) })} /></label>
                        <label>{gettext('Bass')}<input type="range" min="0" max="1" step="0.05" value={settings.bass} onChange={e => track.update({ bass: Number(e.target.value) })} /></label>
                        {preset.subdivision === 4 &&
                            <label>{gettext('Swing')}<input type="range" min="0" max="1" step="0.1" value={preset.swing} onChange={e => track.updateGroove({ swing: Number(e.target.value) })} /></label>
                        }
                    </div>
                    <div className="backing-track-grid" style={{ '--steps': preset.beats * preset.subdivision }}>
                        {DRUM_VOICES.map(voice => (
                            <div className="grid-row" key={voice}>
                                <span className="voice">{voiceName(voice)}</span>
                                {grid[voice].split('').map((cell, step) => (
                                    <button
                                        type="button"
                                        key={step}
                                        className={`cell v-${cell === '.' ? 'off' : cell} ${step % preset.subdivision === 0 ? 'beat' : ''} ${playing && beat && beat.bar >= 0 && Math.floor(step / preset.subdivision) === beat.beat ? 'now' : ''}`}
                                        onClick={() => track.toggleCell(voice, step)}
                                        aria-label={`${voiceName(voice)} ${step + 1}`}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                    {track.modified && <button type="button" className="practice-link" onClick={track.resetGroove}>{gettext('Reset groove')}</button>}
                </div>
            }
        </div>
    );
};

export default BackingTrack;
